import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";

import PersonIcon from "@mui/icons-material/Person";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { setUser, showToast } from "../../store/actions";
import AuthService from "../../service/auth";
import Loading from "../Loading";

export default function AdminLogin() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state);

  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (queryParams.has("message") && queryParams.get("message")) {
      const message = queryParams.get("message");
      dispatch(
        showToast({
          message,
          type: "error",
        })
      );

      queryParams.delete("message");
      navigate("/admin");
    }
  }, [navigate, dispatch, location.search]);

  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check of user is Logged in then redirecting to Admin Dashboard
  useEffect(() => {
    const getAuthUser = async () => {
      const authService = new AuthService();
      const response = await authService.getAuthUser();

      if (response.success) {
        dispatch(setUser(response.data));
        if (response.data.Role_id === 4) {
          navigate("/admin/supplier-dashboard");
        } else {
          navigate("/admin/dashboard");
        }
      } else {
        dispatch(setUser(null));
      }
    };

    getAuthUser();
    if (user.currentUser) {
      if (user.currentUser.Role_id === 4) {
        navigate("/admin/supplier-dashboard");
      } else {
        navigate("/admin/dashboard");
      }
    }

    return () => {
      setLoginId("");
      setPassword("");
    };
  }, [user.currentUser, navigate, dispatch]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setLoading(true);
    const authService = new AuthService();
    const response = await authService.login(loginId, password, 1);

    if (response.success && response.data) {
      dispatch(setUser(response.data.user));

      if (response.data.user.Role_id === 4) {
        navigate("/admin/supplier-dashboard");
      } else {
        navigate("/admin/dashboard");
      }

      setLoginId("");
      setPassword("");
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  if (user.isLoading) return <Loading />;

  return (
    <ThemeProvider theme={theme}>
      <Box
        id="login-page"
        component="main"
        sx={{
          background: (theme) => {
            return theme.palette.TiffanyBlue.gradient;
          },
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflowY: "auto",
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              style={{ background: "#fff", padding: "25px", borderRadius: "10px" }}
            >
              <Typography style={{ fontSize: "28px", fontWeight: "bold", textAlign: "center" }}>ADMIN LOGIN</Typography>

              <TextField
                margin="normal"
                autoComplete="username"
                onChange={(e) => setLoginId(e.target.value)}
                required
                fullWidth
                label="Login ID"
                autoFocus
                InputProps={{
                  endAdornment: <PersonIcon color="primary" />,
                }}
              />
              <TextField
                margin="normal"
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                min="6"
                max="32"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(e) => e.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
              <Button
                className="btn btn-primary"
                color="secondary"
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={!(loginId && password) || loading}
              >
                {loading ? <CircularProgress style={{ width: "20px", height: "20px" }} /> : "Login"}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
